import { Alert, Badge, Button, Card, Label, Spinner } from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import { formatDocument, isValidCPF, maskCEP } from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import { HiInformationCircle } from "react-icons/hi";
import { BOA_VISTA_CONTANTS } from "../../utils/boa-vista-variables";
import HeaderQuery from "./HeaderQuery";
import TitleStripe from "../common/TitleStripe";
import CardQueryDescription from "../common/CardQueryDescription";
import QueryInput from "../inputs/QueryInput";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import AvisoCard from "../common/query/AvisoCard";
import NoOcurrence from "../common/query/NoOcurrence";
import Protestos from "../common/query/Protestos";
import HistoricoConsultas from "../common/query/HistoricoConsultas";
import PendenciasFinanceiras from "../common/query/PendenciasFinanceiras";
import ChequesSustados from "../common/query/ChequesSustados";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import { EvaluationScore } from "../common/query/EvaluationScore";
import FooterQuery from "./FooterQuery";
import ReactApexChart from "react-apexcharts";
import ParticipacaoEmEmpresas from "../common/query/ParticipacaoEmEmpresas";
import AcoesCiveis from "../common/query/AcoesCiveis";
import { useReactToPrint } from "react-to-print";
import { FaPrint } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { GrDocumentText } from "react-icons/gr";
import { FiUserCheck } from "react-icons/fi";
import moment from "moment";
import { LuMapPin } from "react-icons/lu";

const NotaScale = ({ value }) => {
  const nota = String(value || "").toUpperCase();
  const notas = ["A", "B", "C", "D", "E"];
  const colors = {
    A: "bg-emerald-100 text-emerald-700 border-emerald-300",
    B: "bg-lime-100 text-lime-700 border-lime-300",
    C: "bg-amber-100 text-amber-700 border-amber-300",
    D: "bg-orange-100 text-orange-700 border-orange-300",
    E: "bg-rose-100 text-rose-700 border-rose-300",
  };

  if (!notas.includes(nota)) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
        Dados insuficientes para avaliação
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
        <span>Escala</span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
          Nota {nota}
        </span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {notas.map((n) => {
          const active = n === nota;
          return (
            <div
              key={n}
              className={`flex h-10 items-center justify-center rounded-lg border text-sm font-semibold ${
                colors[n]
              } ${active ? "ring-2 ring-blue-600 shadow-sm" : "opacity-60"}`}
            >
              {n}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PositivoPlusCPFQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [document, setDocument] = useState("");
  const [isDoneRequest, setIsDoneRequest] = useState(false);
  const [error, setError] = useState("");
  const [errorQuery, setErrorQuery] = useState("");
  const [data, setData] = useState(null);
  const componentRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: componentRef });

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage?.acertaEssencialPositivo?.consultaCredito);
      setDocument(
        dataFromLocalStorage?.acertaEssencialPositivo?.consultaCredito
          ?.dadosCadastrais?.cpf
      );
      setIsDoneRequest(true);
    }
  }, []);

  const fetchInfo = async (raw) => {
    return await axios(
      `${QUERIES.GET_V2_QUERY}/essencial-plus-cpf/${info.replace(/\D/g, "")}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDocument(info);

    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;

    if (!isValidCPF(unmaskedValue)) {
      setError("CPF inválido!");
      isValid = false;
    }

    if (isValid) {
      try {
        setData(null);
        setIsLoading(true);
        setError("");
        setErrorQuery("");
        setIsDoneRequest(false);

        // Fetch principal
        const { data: dados } = await fetchInfo();
        setData(dados?.data?.acertaEssencialPositivo?.consultaCredito);
        setIsLoading(false);
      } catch (error) {
        setErrorQuery(
          typeof error?.response?.data?.message !== "object"
            ? error?.response?.data?.message
            : "Algum erro ocorreu com sua consulta"
        );
        setIsLoading(false);
      } finally {
        setIsDoneRequest(true);
      }
    } else {
      setData(null);
    }
  };

  const cpfValue = data?.dadosCadastrais?.cpf;
  const cpfFormatted = formatDocument(cpfValue);
  const birthMoment = data?.dadosCadastrais?.dataNascimento
    ? moment(data?.dadosCadastrais?.dataNascimento, [
        "DD/MM/YYYY",
        "YYYY-MM-DD",
      ])
    : null;
  const birthLabel = birthMoment?.isValid()
    ? birthMoment.format("DD/MM/YYYY")
    : null;
  const ageYears =
    birthMoment?.isValid() && birthMoment.isBefore(moment())
      ? moment().diff(birthMoment, "years")
      : null;
  const ageLabel =
    ageYears != null ? `${ageYears} ano${ageYears === 1 ? "" : "s"}` : null;

  return (
    <>
      {!dataFromLocalStorage && (
        <>
          <CardQueryDescription
            title={query?.name || "Informações da consulta"}
            infos={[
              "Dados Cadastrais",
              "Score",
              "Negativações",
              "Protestos",
              "Cheques sem fundos",
              "Cheques sustados",
            ]}
            price={selectedPrice}
          >
            <form
              className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-fit"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col md:flex-row gap-2 items-center">
                <QueryInput
                  info={info}
                  setInfo={setInfo}
                  isLoading={isLoading}
                  error={error}
                  type="CPF"
                />
                <div>
                  {!errorQuery && data && (
                    <Button
                      className="bg-gray-600"
                      onClick={() => reactToPrintFn()}
                    >
                      <div className="flex gap-2 items-center">
                        <FaPrint />
                        <div>Imprimir</div>
                      </div>
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardQueryDescription>

          <SliderToPreview />
        </>
      )}

      {errorQuery ? (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorQuery}</span>
        </Alert>
      ) : (
        <>
          <div
            className={`overflow-x-scroll print-content mobile-fit ${
              data || isLoading ? "block" : "hidden"
            }`}
            ref={componentRef}
          >
            {!isLoading ? (
              <>
                <Card className="p-5 card-pdf relative overflow-hidden">
                  <HeaderQuery
                    title={query?.name}
                    hasHeader={hasHeaderPDF}
                    date={data?.resumoRetorno?.dataConsulta}
                  />

                  <TitleStripe>
                    <div className="flex items-center gap-2">
                      <FiUserCheck className="text-blue-700" />{" "}
                      <span>Dados Cadastrais</span>
                    </div>
                  </TitleStripe>

                  <div className="text-xs">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Nome completo
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {data?.dadosCadastrais?.nome || "Indisponível"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Situação do CPF
                        </p>
                        <span
                          className={`inline-flex items-center rounded-full text-[11px] px-3 py-1 font-semibold ${
                            data?.dadosCadastrais?.situacao?.toUpperCase() ===
                            "REGULAR"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {data?.dadosCadastrais?.situacao || "Indisponível"}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                      {cpfFormatted && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                          CPF: {cpfFormatted}
                        </span>
                      )}
                      {birthLabel && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                          Data Nasc: {birthLabel}
                        </span>
                      )}
                      {ageLabel && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                          {ageLabel}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 grid gap-2 grid-cols-2">
                      {[
                        {
                          label: "Nome da mãe",
                          value:
                            data?.dadosCadastrais?.nomeMae || "Não Informado",
                          hint: "Parente",
                        },
                        {
                          label: "Origem CPF",
                          value:
                            data?.dadosCadastrais?.regiaoCPF || "Não Informado",
                          hint: "Origem",
                        },
                        {
                          label: "Sexo",
                          value: data?.dadosCadastrais?.sexo || "Não Informado",
                          hint: "Gênero",
                        },
                        {
                          label: "Telefone",
                          value:
                            data?.dadosCadastrais?.telefone || "Não Informado",
                          hint: "Contato",
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2"
                        >
                          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                            {item.label}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {item.value}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {item.hint}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <TitleStripe className="mb-4">
                      <div className="flex items-center gap-2">
                        <LuMapPin className="text-blue-700" />{" "}
                        <span>Localização</span>
                      </div>
                    </TitleStripe>
                    <div className="grid gap-2 grid-cols-2">
                      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Endereço
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-[12px] font-semibold text-gray-900">
                            {data?.dadosCadastrais?.endereco || "Não informado"}
                          </p>
                        </div>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Bairro · CEP
                        </p>
                        <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
                          <span>
                            {data?.dadosCadastrais?.bairro || "Não informado"}
                          </span>
                          <span>·</span>
                          <span>
                            {maskCEP(data?.dadosCadastrais?.cep) || "--"}
                          </span>
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          Cidade
                        </p>
                        <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
                          <span>
                            {data?.dadosCadastrais?.cidade || "Não informado"}
                          </span>
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                          UF
                        </p>
                        <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
                          <span>
                            {data?.dadosCadastrais?.uf || "Não informado"}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <TitleStripe className="mb-5">
                      Participação Societária
                    </TitleStripe>
                    {data?.participacaoEmEmpresas?.length > 0 ? (
                      <ParticipacaoEmEmpresas
                        empresas={data?.participacaoEmEmpresas}
                      />
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <TitleStripe>Quadro de avisos</TitleStripe>
                  <div className="container-alerts three-columns">
                    <AvisoCard
                      title="Negativações"
                      hasRestrictions={
                        +data?.resumoConsulta?.pendenciasFinanceiras
                          ?.quantidadeTotal > 0 ||
                        data?.resumoConsulta?.pendenciasFinanceiras
                          ?.valorTotal != "0"
                      }
                      messageError={`${data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal} ocorrências`}
                      value={`${data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal}`
                        ?.replaceAll(".", "")
                        ?.replace(",", ".")}
                    />

                    <AvisoCard
                      title="Cheques Sustados"
                      hasRestrictions={
                        +data?.resumoConsulta?.chequesSustados
                          ?.quantidadeTotal > 0
                      }
                      messageError={`${data?.resumoConsulta?.chequesSustados?.quantidadeTotal}
                      ocorrências`}
                    />

                    <AvisoCard
                      title="Cheques sem Fundos"
                      hasRestrictions={
                        +data?.resumoConsulta?.chequesSemFundo
                          ?.quantidadeTotal > 0
                      }
                      messageError={`${data?.resumoConsulta?.chequesSemFundo?.quantidadeTotal}
                      ocorrências`}
                    />

                    <AvisoCard
                      title="Protestos"
                      hasRestrictions={
                        +data?.resumoConsulta?.protestos?.quantidadeTotal > 0
                      }
                      messageError={`${data?.resumoConsulta?.protestos?.quantidadeTotal} ocorrências`}
                    />

                    <AvisoCard
                      title="Ações Cíveis"
                      hasRestrictions={
                        +data?.resumoConsulta?.acoesCiveis?.quantidadeTotal > 0
                      }
                      messageError={`${data?.resumoConsulta?.acoesCiveis?.quantidadeTotal} ocorrências`}
                    />
                  </div>

                  <div>
                    <TitleStripe>Avaliação preliminar de crédito</TitleStripe>
                  </div>
                  <EvaluationScore
                    data={data}
                    score={data?.score?.score}
                    probability={data?.score?.probabilidade}
                  />

                  {data?.faturasEmAtraso?.nota && (
                    <div>
                      <TitleStripe className="mb-4">
                        <div className="flex items-center gap-2">
                          <IoReceiptOutline className="text-blue-700" />
                          <span>Faturas em atraso</span>
                        </div>
                      </TitleStripe>
                      <div className="-mt-2 mb-2 text-sm">
                        Indica uma nota para o máximo de dias que 1 fatura ficou
                        em atraso nos últimos 6 meses.
                      </div>

                      <NotaScale value={data?.faturasEmAtraso?.nota} />
                      <div className="mt-2 text-sm">
                        Quanto mais próximo o consumidor estiver da faixa{" "}
                        <span className="font-semibold">A</span>, menos dias a
                        fatura está ou ficou em atraso e quanto mais próximo da
                        faixa <span className="font-semibold">E</span>, mais
                        dias a fatura ficou em atraso.
                      </div>
                    </div>
                  )}

                  {data?.contratosRecentes?.nota && (
                    <div>
                      <TitleStripe className="mb-4">
                        <div className="flex items-center gap-2">
                          <GrDocumentText className="text-blue-700" />
                          <span>Contratos recentes</span>
                        </div>
                      </TitleStripe>
                      <div className="-mt-2 mb-2 text-sm">
                        Indica uma nota para o nível de comprometimento de
                        crédito do consumidor, com base na quantidade de
                        contratos fechados nos últimos 6 meses.
                      </div>

                      <NotaScale value={data?.contratosRecentes?.nota} />
                      <div className="mt-2 text-sm">
                        Quanto mais próximo da faixa{" "}
                        <span className="font-semibold">A</span>, menos
                        contratos foram fechados e quanto mais perto da faixa{" "}
                        <span className="font-semibold">E</span>, mais contratos
                        foram fechados nos últimos 6 meses.
                      </div>
                    </div>
                  )}

                  <div>
                    <TitleStripe className="mb-5">Negativações</TitleStripe>

                    {data?.pendenciasFinanceiras?.length > 0 ? (
                      <>
                        <PendenciasFinanceiras
                          quantidadeTotal={
                            data?.resumoConsulta?.pendenciasFinanceiras
                              ?.quantidadeTotal
                          }
                          valorTotal={
                            data?.resumoConsulta?.pendenciasFinanceiras
                              ?.valorTotal
                          }
                          pendenciasFinanceiras={data?.pendenciasFinanceiras}
                        />
                      </>
                    ) : (
                      <NoOcurrence />
                    )}

                    <div>
                      <TitleStripe className="mb-5">
                        Cheques sustados
                      </TitleStripe>

                      {data?.chequesSustados?.length > 0 ? (
                        <>
                          <ChequesSustados
                            chequesSustados={data?.chequesSustados}
                          />
                        </>
                      ) : (
                        <NoOcurrence />
                      )}
                    </div>
                    <div>
                      <TitleStripe className="mb-5">
                        Cheques sem fundos
                      </TitleStripe>

                      {data?.chequesSemFundo?.length > 0 ? (
                        <>
                          <ChequesSemFundos
                            chequesSemFundos={data?.chequesSemFundo}
                          />
                        </>
                      ) : (
                        <NoOcurrence />
                      )}
                    </div>

                    <div>
                      <TitleStripe className="mb-5">
                        Títulos protestados
                      </TitleStripe>
                      {data?.protestos?.length > 0 ? (
                        <Protestos
                          quantidadeTotal={
                            data?.resumoConsulta?.protestos?.quantidadeTotal
                          }
                          valorTotal={
                            data?.resumoConsulta?.protestos?.valorTotal
                          }
                          protestos={data?.protestos}
                        />
                      ) : (
                        <NoOcurrence />
                      )}
                    </div>
                  </div>

                  <div>
                    <TitleStripe className="mb-5">Ações cíveis</TitleStripe>
                    {data?.acoesCiveis?.length > 0 ? (
                      <AcoesCiveis
                        quantidadeTotal={
                          data?.resumoConsulta?.acoesCiveis?.quantidadeTotal
                        }
                        acoesCiveis={data?.acoesCiveis}
                      />
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-5">
                      Histórico de consultas
                    </TitleStripe>

                    {data?.historicoConsultas?.length > 0 ? (
                      <div className="flex gap-2 items-start">
                        <div className="flex-1">
                          <HistoricoConsultas
                            historicoConsultas={data?.historicoConsultas?.slice(
                              0,
                              5
                            )}
                          />
                        </div>
                        {data?.historicoConsultasResumo
                          ?.paresMesAnoHistoricoConsultas?.length > 0 && (
                          <ReactApexChart
                            className="border border-gray-200 rounded-md w-2/5 mt-3"
                            options={{
                              chart: {
                                height: 150,
                                type: "bar",
                                toolbar: {
                                  show: false, // Remove o menu de ações
                                },
                              },
                              plotOptions: {
                                bar: {
                                  borderRadius: 2,
                                  dataLabels: {
                                    position: "center", // top, center, bottom
                                  },
                                },
                              },
                              dataLabels: {
                                enabled: true,
                                formatter: function (val) {
                                  return val;
                                },
                                offsetY: 0,
                                style: {
                                  fontSize: "12px",
                                  colors: ["white"],
                                },
                              },

                              xaxis: {
                                categories:
                                  data?.historicoConsultasResumo
                                    ?.paresMesAnoHistoricoConsultas,
                                position: "top",
                                axisBorder: {
                                  show: false,
                                },
                                axisTicks: {
                                  show: false,
                                },
                                crosshairs: {
                                  fill: {
                                    type: "gradient",
                                    gradient: {
                                      colorFrom: "#309ADB",
                                      colorTo: "#309ADB",
                                      stops: [0, 100],
                                      opacityFrom: 0.4,
                                      opacityTo: 0.5,
                                    },
                                  },
                                },
                                tooltip: {
                                  enabled: true,
                                },
                              },
                              yaxis: {
                                axisBorder: {
                                  show: false,
                                },
                                axisTicks: {
                                  show: false,
                                },
                                labels: {
                                  show: false,
                                  formatter: function (val) {
                                    return val;
                                  },
                                },
                              },
                              title: {
                                text: "Últimos 90 dias",
                                floating: true,
                                offsetY: 155,
                                align: "center",
                                style: {
                                  color: "#444",
                                },
                              },
                            }}
                            series={[
                              {
                                name: "Qtd. Consultas no período",
                                data: data?.historicoConsultasResumo
                                  ?.totaisHistoricoConsultas,
                              },
                            ]}
                            type="bar"
                            height={175}
                          />
                        )}
                      </div>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <FooterQuery hasHeader={hasHeaderPDF} />
                </Card>
              </>
            ) : (
              <QueryLoading />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default PositivoPlusCPFQuery;
